import { handleRequest } from "../../axios";
import { axiosInstance } from "./JsdhNotifikaceService";
import {
    ADMIN_INVENTAR_KATEGORIE_URL,
    ADMIN_INVENTAR_KATEGORIE_BY_ID_URL,
    ADMIN_INVENTAR_KATEGORIE_REORDER_URL,
    ADMIN_INVENTAR_VELIKOST_TYPY_URL,
    ADMIN_INVENTAR_VELIKOST_TYP_BY_ID_URL,
    ADMIN_INVENTAR_TISK_URL,
    ADMIN_INVENTAR_POLOZKY_URL,
    ADMIN_INVENTAR_POLOZKA_BY_ID_URL,
    ADMIN_INVENTAR_POLOZKA_HISTORIE_URL,
    ADMIN_INVENTAR_POLOZKA_PRIRAZENI_URL,
    ADMIN_INVENTAR_POLOZKA_VYDEJ_URL,
    ADMIN_INVENTAR_POLOZKA_VRACENI_URL,
    ADMIN_INVENTAR_POLOZKA_PRIRAZENI_VOZIDLO_URL,
    ADMIN_INVENTAR_POLOZKA_FOTKY_URL,
    ADMIN_INVENTAR_POLOZKA_FOTKA_URL,
    ADMIN_INVENTAR_POLOZKA_FOTKA_DATA_URL,
    ADMIN_INVENTAR_VYSTROJNI_KARTA_URL,
    ADMIN_INVENTAR_CEKAJICI_NA_CISLO_URL,
    ADMIN_INVENTAR_STATISTIKY_URL,
    ADMIN_INVENTAR_KONFIGURACE_URL,
    ADMIN_INVENTAR_DOPLNENI_CISEL_URL,
    ADMIN_INVENTAR_INVENTURA_URL,
    ADMIN_INVENTAR_INVENTURA_BY_ID_URL,
    ADMIN_INVENTAR_INVENTURA_POLOZKY_URL,
    ADMIN_INVENTAR_INVENTURA_SKEN_URL,
    ADMIN_INVENTAR_INVENTURA_DOKONCIT_URL,
    ADMIN_INVENTAR_INVENTURA_CHYBEJICI_URL,
    ADMIN_INVENTAR_INVENTURA_NALEZENA_URL,
    ADMIN_INVENTAR_POLOZKA_KONTROLY_URL,
    ADMIN_INVENTAR_POLOZKA_KONTROLY_HISTORIE_URL,
    ADMIN_INVENTAR_POLOZKA_KONTROLA_URL,
    INVENTAR_MOJE_URL,
} from "../url";
import type {
    InventarKategorie,
    InventarPolozka,
    InventarHistorie,
    InventarPrirazeni,
    VystrojniKartaResponse,
    MojeVystrojResponse,
    CreateInventarPolozkaRequest,
    UpdateInventarPolozkaRequest,
    VydejInventarPolozkyRequest,
    VraceniInventarPolozkyRequest,
    InventarKontrolaStatusResponse,
    InventarPolozkaKontrola,
    CreateInventarKontrolaRequest,
    InventuraRequest,
    SkenPolozkyRequest,
    NalezenaPolozkaRequest,
    InventarInventura,
    InventarInventuraPolozka,
} from "../generated/models/Inventar";

// === Kategorie ===

const getAllKategorie = () => {
    return handleRequest<InventarKategorie[]>(() =>
        axiosInstance.get(ADMIN_INVENTAR_KATEGORIE_URL)
    );
};

const createKategorie = (data: Partial<InventarKategorie>) => {
    return handleRequest<InventarKategorie>(() =>
        axiosInstance.post(ADMIN_INVENTAR_KATEGORIE_URL, data)
    );
};

const updateKategorie = (id: number, data: Partial<InventarKategorie>) => {
    return handleRequest(() =>
        axiosInstance.put(ADMIN_INVENTAR_KATEGORIE_BY_ID_URL(id), data)
    );
};

const reorderKategorie = (orderedIds: number[]) => {
    return handleRequest(() =>
        axiosInstance.post(ADMIN_INVENTAR_KATEGORIE_REORDER_URL, orderedIds)
    );
};

const deleteKategorie = (id: number) => {
    return handleRequest(() =>
        axiosInstance.delete(ADMIN_INVENTAR_KATEGORIE_BY_ID_URL(id))
    );
};

// === Položky ===

const getAllPolozky = (params?: { kategorieId?: number; vozidloId?: number; clenId?: number }) => {
    return handleRequest<InventarPolozka[]>(() =>
        axiosInstance.get(ADMIN_INVENTAR_POLOZKY_URL, { params })
    );
};

const getPolozkaById = (id: number) => {
    return handleRequest<InventarPolozka>(() =>
        axiosInstance.get(ADMIN_INVENTAR_POLOZKA_BY_ID_URL(id))
    );
};

const createPolozka = (data: CreateInventarPolozkaRequest) => {
    return handleRequest<InventarPolozka>(() =>
        axiosInstance.post(ADMIN_INVENTAR_POLOZKY_URL, data)
    );
};

const updatePolozka = (id: number, data: UpdateInventarPolozkaRequest) => {
    return handleRequest<InventarPolozka>(() =>
        axiosInstance.put(ADMIN_INVENTAR_POLOZKA_BY_ID_URL(id), data)
    );
};

const deletePolozka = (id: number, duvod?: string) => {
    return handleRequest(() =>
        axiosInstance.delete(ADMIN_INVENTAR_POLOZKA_BY_ID_URL(id), { params: { duvod } })
    );
};

// === Foto ===

export interface InventarFotoMeta {
    id: number;
    polozkaId: number;
    contentType: string;
    velikostBajtu: number;
    poradi: number;
}

const getFotky = (id: number) =>
    handleRequest<InventarFotoMeta[]>(() =>
        axiosInstance.get(ADMIN_INVENTAR_POLOZKA_FOTKY_URL(id))
    );

const uploadFotografie = (id: number, soubor: Blob, nazev: string) => {
    const formData = new FormData();
    formData.append('file', soubor, nazev);
    return handleRequest<InventarFotoMeta>(() =>
        axiosInstance.post(ADMIN_INVENTAR_POLOZKA_FOTKY_URL(id), formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    );
};

const deleteFotografie = (id: number, fotoId: number) =>
    handleRequest<void>(() =>
        axiosInstance.delete(ADMIN_INVENTAR_POLOZKA_FOTKA_URL(id, fotoId))
    );

/** Obrazek jde pres autentizovany endpoint, takze se stahuje jako blob. */
const getFotkaBlob = (id: number, fotoId: number) =>
    handleRequest<Blob>(() =>
        axiosInstance.get(ADMIN_INVENTAR_POLOZKA_FOTKA_DATA_URL(id, fotoId), { responseType: 'blob' })
    );

// === Historie ===

const getHistorie = (polozkaId: number) => {
    return handleRequest<InventarHistorie[]>(() =>
        axiosInstance.get(ADMIN_INVENTAR_POLOZKA_HISTORIE_URL(polozkaId))
    );
};

const getPrirazeniHistorie = (polozkaId: number) => {
    return handleRequest<InventarPrirazeni[]>(() =>
        axiosInstance.get(ADMIN_INVENTAR_POLOZKA_PRIRAZENI_URL(polozkaId))
    );
};

// === Výdej / Vrácení ===

const vydejPolozku = (polozkaId: number, data: VydejInventarPolozkyRequest) => {
    return handleRequest<InventarPrirazeni>(() =>
        axiosInstance.post(ADMIN_INVENTAR_POLOZKA_VYDEJ_URL(polozkaId), data)
    );
};

const vratPolozku = (polozkaId: number, data: VraceniInventarPolozkyRequest) => {
    return handleRequest(() =>
        axiosInstance.post(ADMIN_INVENTAR_POLOZKA_VRACENI_URL(polozkaId), data)
    );
};

// === Přiřazení vozidlu/stanici ===

const zmenaPrirazeniVozidlo = (polozkaId: number, prirazeniTyp: string, prirazeniId?: number) => {
    return handleRequest(() =>
        axiosInstance.post(ADMIN_INVENTAR_POLOZKA_PRIRAZENI_VOZIDLO_URL(polozkaId), null, {
            params: { prirazeniTyp, prirazeniId },
        })
    );
};

// === Výstrojní karta ===

const getVystrojniKarta = (uzivatelId: number) => {
    return handleRequest<VystrojniKartaResponse>(() =>
        axiosInstance.get(ADMIN_INVENTAR_VYSTROJNI_KARTA_URL(uzivatelId))
    );
};

const downloadVystrojniKartaPdf = async (uzivatelId: number) => {
    try {
        const res = await axiosInstance.get(ADMIN_INVENTAR_VYSTROJNI_KARTA_URL(uzivatelId), {
            responseType: 'blob',
            headers: { 'Accept': 'application/pdf' },
        });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `vystrojni-karta-${uzivatelId}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Chyba při stahování PDF výstrojní karty:', error);
        throw new Error('Nepodařilo se stáhnout PDF výstrojní karty.');
    }
};

// === Statistiky ===

const getCekajiciNaInvCislo = () => {
    return handleRequest<InventarPolozka[]>(() =>
        axiosInstance.get(ADMIN_INVENTAR_CEKAJICI_NA_CISLO_URL)
    );
};

const getStatistiky = () => {
    return handleRequest<{ aktivnichPolozek: number; cekajicichNaCislo: number }>(() =>
        axiosInstance.get(ADMIN_INVENTAR_STATISTIKY_URL)
    );
};

// === Typy velikostí ===

const getAllVelikostTypy = () => {
    return handleRequest<any[]>(() => axiosInstance.get(ADMIN_INVENTAR_VELIKOST_TYPY_URL));
};

const createVelikostTyp = (data: any) => {
    return handleRequest<any>(() => axiosInstance.post(ADMIN_INVENTAR_VELIKOST_TYPY_URL, data));
};

const updateVelikostTyp = (id: number, data: any) => {
    return handleRequest(() => axiosInstance.put(ADMIN_INVENTAR_VELIKOST_TYP_BY_ID_URL(id), data));
};

const deleteVelikostTyp = (id: number) => {
    return handleRequest(() => axiosInstance.delete(ADMIN_INVENTAR_VELIKOST_TYP_BY_ID_URL(id)));
};

// === Konfigurace inventárních čísel ===

export interface InventarKonfigurace {
    cisloZdroj: string;
    cisloFormat: string;
    cisloSekvencePer: string;
    cisloPovinne: boolean;
    nahledCisla: string;
    notifikaceOopEnabled: boolean | null;
}

const getKonfigurace = () => {
    return handleRequest<InventarKonfigurace>(() =>
        axiosInstance.get(ADMIN_INVENTAR_KONFIGURACE_URL)
    );
};

const updateKonfigurace = (data: { cisloZdroj: string; cisloFormat?: string; cisloSekvencePer?: string; cisloPovinne?: boolean; notifikaceOopEnabled?: boolean | null }) => {
    return handleRequest(() =>
        axiosInstance.put(ADMIN_INVENTAR_KONFIGURACE_URL, data)
    );
};

const doplnInventarniCisla = (data: { polozky: Array<{ polozkaId: number; inventarniCislo: string; datumZarazeniUctarna?: string; ucetniKategorie?: string }> }) => {
    return handleRequest(() =>
        axiosInstance.post(ADMIN_INVENTAR_DOPLNENI_CISEL_URL, data)
    );
};

// === Inventura ===

const getInventury = () => {
    return handleRequest<InventarInventura[]>(() => axiosInstance.get(ADMIN_INVENTAR_INVENTURA_URL));
};

const zahajitInventuru = (data: InventuraRequest) => {
    return handleRequest<InventarInventura>(() => axiosInstance.post(ADMIN_INVENTAR_INVENTURA_URL, data));
};

const getInventura = (id: number) => {
    return handleRequest<InventarInventura>(() => axiosInstance.get(ADMIN_INVENTAR_INVENTURA_BY_ID_URL(id)));
};

const getPolozkyInventury = (id: number) => {
    return handleRequest<InventarInventuraPolozka[]>(() => axiosInstance.get(ADMIN_INVENTAR_INVENTURA_POLOZKY_URL(id)));
};

const skenPolozky = (inventuraId: number, data: SkenPolozkyRequest) => {
    return handleRequest<{ message: string }>(() => axiosInstance.post(ADMIN_INVENTAR_INVENTURA_SKEN_URL(inventuraId), data));
};

const oznacNalezena = (inventuraId: number, polozkaId: number, data: NalezenaPolozkaRequest) => {
    return handleRequest<{ message: string }>(() => axiosInstance.post(ADMIN_INVENTAR_INVENTURA_NALEZENA_URL(inventuraId, polozkaId), data));
};

const dokoncitInventuru = (id: number) => {
    return handleRequest<InventarInventura>(() => axiosInstance.post(ADMIN_INVENTAR_INVENTURA_DOKONCIT_URL(id)));
};

const getChybejiciPolozky = (id: number) => {
    return handleRequest<InventarInventuraPolozka[]>(() => axiosInstance.get(ADMIN_INVENTAR_INVENTURA_CHYBEJICI_URL(id)));
};

// === Revize OOP (kontroly položek) ===

const getKontrolyForPolozka = (id: number) =>
    handleRequest<InventarKontrolaStatusResponse[]>(() => axiosInstance.get(ADMIN_INVENTAR_POLOZKA_KONTROLY_URL(id)));

const getKontrolyHistorie = (id: number, typId: number) =>
    handleRequest<InventarPolozkaKontrola[]>(() => axiosInstance.get(ADMIN_INVENTAR_POLOZKA_KONTROLY_HISTORIE_URL(id, typId)));

const createPolozkaKontrola = (id: number, data: CreateInventarKontrolaRequest) =>
    handleRequest<InventarPolozkaKontrola>(() => axiosInstance.post(ADMIN_INVENTAR_POLOZKA_KONTROLY_URL(id), data));

const deletePolozkaKontrola = (id: number, kontrolaId: number) =>
    handleRequest(() => axiosInstance.delete(ADMIN_INVENTAR_POLOZKA_KONTROLA_URL(id, kontrolaId)));

// === Moje vybavení (member) ===

const getMojeVybaveni = () =>
    handleRequest<MojeVystrojResponse>(() => axiosInstance.get(INVENTAR_MOJE_URL));

// === Hromadný tisk ===

const downloadTiskPdf = async (rezim: 'QR' | 'SEZNAM', ids?: number[], kategorieId?: number, format?: string) => {
    const params: Record<string, string> = { rezim };
    if (ids && ids.length > 0) {
        params.ids = ids.join(',');
    } else if (kategorieId) {
        params.kategorieId = String(kategorieId);
    }
    if (format) {
        params.format = format;
    }
    try {
        const res = await axiosInstance.get(ADMIN_INVENTAR_TISK_URL, {
            responseType: 'blob',
            params,
        });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = rezim === 'QR' ? 'qr-stitky.pdf' : 'inventarni-seznam.pdf';
        a.click();
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Chyba při stahování PDF tisku inventáře:', error);
        throw new Error(rezim === 'QR' ? 'Nepodařilo se vytisknout QR štítky.' : 'Nepodařilo se vytisknout inventární seznam.');
    }
};

const InventarServiceApi = {
    getAllKategorie,
    createKategorie,
    updateKategorie,
    deleteKategorie,
    reorderKategorie,
    getAllVelikostTypy,
    createVelikostTyp,
    updateVelikostTyp,
    deleteVelikostTyp,
    getAllPolozky,
    getPolozkaById,
    createPolozka,
    updatePolozka,
    deletePolozka,
    uploadFotografie,
    deleteFotografie,
    getFotky,
    getFotkaBlob,
    getHistorie,
    getPrirazeniHistorie,
    vydejPolozku,
    vratPolozku,
    zmenaPrirazeniVozidlo,
    getVystrojniKarta,
    downloadVystrojniKartaPdf,
    downloadTiskPdf,
    getCekajiciNaInvCislo,
    getStatistiky,
    getKonfigurace,
    updateKonfigurace,
    doplnInventarniCisla,
    getInventury,
    zahajitInventuru,
    getInventura,
    getPolozkyInventury,
    skenPolozky,
    oznacNalezena,
    dokoncitInventuru,
    getChybejiciPolozky,
    getKontrolyForPolozka,
    getKontrolyHistorie,
    createPolozkaKontrola,
    deletePolozkaKontrola,
    getMojeVybaveni,
};

export default InventarServiceApi;
